<template>
  <div class="modal fade" id="modalDetalleEvento" tabindex="-1" ref="modalEl">
    <div class="modal-dialog modal-md modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content divisan-modal border-0">
        <!-- ── Header ── -->
        <div class="modal-header border-0 pb-0">
          <div class="flex-grow-1">
            <div class="d-flex align-items-center gap-2 flex-wrap mb-1">
              <h5 class="fw-bold text-cafe mb-0">{{ grupo?.maquina_nombre }}</h5>
              <span :class="['badge-estatus', clsEstatus(grupo?.estatus_grupo)]">
                <i :class="['bi me-1', iconoEstatus(grupo?.estatus_grupo)]"></i>
                {{ labelEstatus(grupo?.estatus_grupo) }}
              </span>
            </div>
            <div class="d-flex gap-2 flex-wrap">
              <span class="info-chip">
                <i class="bi bi-calendar3 me-1"></i
                >{{ formatFecha(grupo?.fecha_programada) }}
              </span>
              <span class="info-chip">
                <i class="bi bi-tools me-1"></i>{{ grupo?.total_rutinas || 1 }} rutina(s)
              </span>
            </div>
          </div>
          <button type="button" class="btn-close ms-2" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body pt-2">
          <!-- Una sola rutina: sin tabs -->
          <template v-if="!grupo?.calendarios || grupo.calendarios.length === 1">
            <PanelCalendario :calendario="calendarioActivo" />
          </template>

          <!-- Múltiples rutinas: tabs -->
          <template v-else>
            <ul class="nav nav-tabs divisan-tabs mb-3">
              <li v-for="(cal, idx) in grupo.calendarios" :key="cal.id" class="nav-item">
                <button
                  :class="['nav-link', { active: tabActivo === idx }]"
                  @click="tabActivo = idx"
                >
                  <span
                    class="semaforo-mini me-1"
                    :style="{ background: colorEstatus(cal.estatus) }"
                  ></span>
                  {{ cal.rutina_nombre }}
                </button>
              </li>
            </ul>
            <PanelCalendario :calendario="grupo.calendarios[tabActivo]" />
          </template>
        </div>

        <!-- ── Footer ── -->
        <div
          class="modal-footer border-0 pt-0 d-flex justify-content-between align-items-center"
        >
          <small v-if="grupo?.estatus_grupo === 'COMPLETADO'" class="text-verde fw-bold">
            <i class="bi bi-check-circle-fill me-1"></i>Todas las rutinas completadas
          </small>
          <small
            v-else-if="grupo?.estatus_grupo === 'ATRASADO'"
            class="text-danger fw-bold"
          >
            <i class="bi bi-exclamation-triangle-fill me-1"></i>Período vencido
          </small>
          <small
            v-else-if="grupo?.estatus_grupo === 'EN_PROCESO'"
            class="text-info-dark fw-bold"
          >
            <i class="bi bi-play-circle-fill me-1"></i>En proceso — gestionar en Mtto.
            Preventivo
          </small>
          <span v-else></span>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            data-bs-dismiss="modal"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineComponent, h } from "vue";
import { Modal } from "bootstrap";
import { COLOR_ESTATUS } from "@/store/calendarioStore";

// ── PanelCalendario como componente de render function (sin template string) ──
// Esto evita el error "runtime compilation is not supported" al usar
// la option API con template: `...` en builds sin compilador de Vue.
const PanelCalendario = defineComponent({
  name: "PanelCalendario",
  props: {
    calendario: { type: Object, default: null },
  },
  setup(props) {
    function clsEstatus(v) {
      return (v || "").toLowerCase().replace("_", "-");
    }
    function iconoEstatus(v) {
      return (
        {
          PENDIENTE: "bi-hourglass",
          EN_PROCESO: "bi-play-circle-fill",
          COMPLETADO: "bi-check-circle-fill",
          ATRASADO: "bi-exclamation-triangle-fill",
        }[v] || "bi-circle"
      );
    }
    function formatFecha(f) {
      if (!f) return "—";
      return new Date(f + "T12:00:00").toLocaleDateString("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
    function actividadesOrdenadas(cal) {
      return [...(cal?.actividades || [])].sort((a, b) => a.orden - b.orden);
    }
    function totalActividades(cal) {
      return cal?.actividades?.length || 0;
    }
    function actividadesCompletadas(cal) {
      return (cal?.actividades || []).filter((a) => a.completado).length;
    }
    function pct(cal) {
      const t = totalActividades(cal);
      return t ? Math.round((actividadesCompletadas(cal) / t) * 100) : 0;
    }

    return () => {
      const cal = props.calendario;
      if (!cal) return h("div");

      const esPendiente = cal.estatus === "PENDIENTE";
      const mensajeReadonly =
        {
          EN_PROCESO:
            "Esta rutina ya está en proceso. Gestiona sus actividades en Mtto. Preventivo.",
          COMPLETADO: "Esta rutina ya fue completada. No se puede modificar.",
          ATRASADO: "Esta rutina tiene el período vencido.",
        }[cal.estatus] || "";

      const actsOrdenadas = actividadesOrdenadas(cal);
      const totalActs = totalActividades(cal);
      const completadas = actividadesCompletadas(cal);
      const porcentaje = pct(cal);

      return h("div", [
        // Aviso readonly
        !esPendiente && mensajeReadonly
          ? h("div", { class: ["aviso-readonly", clsEstatus(cal.estatus)] }, [
              h("i", { class: ["bi me-2", iconoEstatus(cal.estatus)] }),
              h("span", mensajeReadonly),
            ])
          : null,

        // Chips de info
        h("div", { class: "d-flex gap-2 flex-wrap mb-3" }, [
          cal.rutina_area
            ? h("span", { class: "info-chip-sm" }, [
                h("i", { class: "bi bi-geo-alt me-1" }),
                cal.rutina_area,
              ])
            : null,
          h("span", { class: "info-chip-sm" }, [
            h("i", { class: "bi bi-flag me-1" }),
            "Límite: " + formatFecha(cal.fecha_limite),
          ]),
        ]),

        // Título sección actividades
        h("div", { class: "seccion-titulo mb-2" }, [
          h("i", { class: "bi bi-list-check me-2" }),
          "Actividades",
          h(
            "span",
            { class: "ms-auto text-cafe opacity-50 small fw-normal" },
            `${completadas}/${totalActs} completadas`
          ),
        ]),

        // Barra progreso
        h("div", { class: "mb-3" }, [
          h("div", { class: "progress", style: "height:6px;border-radius:6px" }, [
            h("div", {
              class: "progress-bar bg-verde",
              style: { width: porcentaje + "%" },
            }),
          ]),
        ]),

        // Lista actividades
        actsOrdenadas.length
          ? h(
              "div",
              { class: "actividades-lista mb-3" },
              actsOrdenadas.map((act) =>
                h(
                  "div",
                  {
                    key: act.id,
                    class: ["actividad-row", { completada: act.completado }],
                  },
                  [
                    h("i", {
                      class: [
                        "bi estado-icono",
                        act.completado
                          ? "bi-check-circle-fill text-verde"
                          : "bi-dash-circle text-muted opacity-50",
                      ],
                    }),
                    h("div", { class: "flex-grow-1" }, [
                      h(
                        "span",
                        {
                          class: [
                            "act-nombre",
                            { "text-decoration-line-through opacity-50": act.completado },
                          ],
                        },
                        [
                          h("span", { class: "orden-dot" }, String(act.orden)),
                          " " + act.actividad_nombre,
                        ]
                      ),
                      act.reprogramada
                        ? h("div", { class: "reprog-chip mt-1" }, [
                            h("i", { class: "bi bi-calendar2-check me-1" }),
                            "Reprogramada → " + formatFecha(act.nueva_fecha),
                          ])
                        : null,
                      act.observaciones
                        ? h("div", { class: "obs-chip mt-1" }, [
                            h("i", { class: "bi bi-chat-left-text me-1" }),
                            act.observaciones,
                          ])
                        : null,
                    ]),
                  ]
                )
              )
            )
          : h(
              "p",
              { class: "small text-cafe opacity-40" },
              "Sin actividades registradas"
            ),
      ]);
    };
  },
});

// ── Props ─────────────────────────────────────────────────────
const props = defineProps({
  evento: { type: Object, default: null },
});

const modalEl = ref(null);
let modalInstance = null;
const tabActivo = ref(0);

// ── Normalizar entrada ─────────────────────────────────────────
// Soporta grupo nuevo (_esGrupo=true) y evento individual antiguo
const grupo = computed(() => {
  const ev = props.evento;
  if (!ev) return null;
  if (ev._esGrupo || ev.grupo_id) return ev;
  // Compatibilidad: evento individual envuelto como grupo de 1
  return {
    grupo_id: `${ev.maquina}_${ev.fecha_programada}`,
    maquina: ev.maquina,
    maquina_nombre: ev.maquina_nombre,
    fecha_programada: ev.fecha_programada,
    estatus_grupo: ev.estatus,
    total_rutinas: 1,
    calendarios: [ev],
    _esGrupo: false,
  };
});

const calendarioActivo = computed(() => grupo.value?.calendarios?.[0] || null);

// ── Helpers ────────────────────────────────────────────────────
const LABELS = {
  PENDIENTE: "Pendiente",
  EN_PROCESO: "En proceso",
  COMPLETADO: "Completado",
  ATRASADO: "Atrasado",
};
const ICONOS = {
  PENDIENTE: "bi-hourglass",
  EN_PROCESO: "bi-play-circle-fill",
  COMPLETADO: "bi-check-circle-fill",
  ATRASADO: "bi-exclamation-triangle-fill",
};

const labelEstatus = (v) => LABELS[v] || v || "";
const iconoEstatus = (v) => ICONOS[v] || "bi-circle";
const clsEstatus = (v) => (v || "").toLowerCase().replace("_", "-");
const colorEstatus = (v) => COLOR_ESTATUS[v]?.bg || COLOR_ESTATUS.PENDIENTE.bg;

function formatFecha(f) {
  if (!f) return "—";
  return new Date(f + "T12:00:00").toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// ── Abrir / cerrar ─────────────────────────────────────────────
function abrir() {
  tabActivo.value = 0;
  modalInstance?.show();
}

function cerrar() {
  modalInstance?.hide();
}

onMounted(() => {
  modalInstance = new Modal(modalEl.value);
});
defineExpose({ abrir, cerrar });
</script>

<style scoped>
.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.text-info-dark {
  color: #084298;
}

/* Tabs */
.divisan-tabs {
  border-bottom: 2px solid rgba(96, 108, 56, 0.2);
}
.divisan-tabs .nav-link {
  color: var(--d-brown);
  font-weight: 600;
  font-size: 0.82rem;
  border: none;
  border-bottom: 3px solid transparent;
  padding: 0.4rem 0.8rem;
  background: none;
  transition: all 0.2s;
  border-radius: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.divisan-tabs .nav-link:hover {
  color: var(--d-green);
  background: rgba(96, 108, 56, 0.06);
}
.divisan-tabs .nav-link.active {
  color: var(--d-brown);
  border-bottom: 3px solid var(--d-brown);
  background: transparent;
}
.semaforo-mini {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
}

/* Badges / chips header */
.badge-estatus {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 800;
}
.badge-estatus.pendiente {
  background: #fff3cd;
  color: #856404;
}
.badge-estatus.en-proceso {
  background: #cfe2ff;
  color: #084298;
}
.badge-estatus.completado {
  background: #d1e7dd;
  color: #0f5132;
}
.badge-estatus.atrasado {
  background: #f8d7da;
  color: #842029;
}
.info-chip {
  background: rgba(64, 34, 8, 0.08);
  color: var(--d-brown);
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

/* Estilos del PanelCalendario — deben ser :deep para alcanzar nodos del render fn */
:deep(.info-chip-sm) {
  background: rgba(96, 108, 56, 0.1);
  color: var(--d-green-dark);
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 600;
}

:deep(.aviso-readonly) {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  border-radius: 10px;
  padding: 0.65rem 0.9rem;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 1rem;
  line-height: 1.4;
}
:deep(.aviso-readonly.en-proceso) {
  background: #cfe2ff;
  color: #084298;
  border: 1px solid rgba(21, 101, 192, 0.25);
}
:deep(.aviso-readonly.completado) {
  background: #d1e7dd;
  color: #0f5132;
  border: 1px solid rgba(96, 108, 56, 0.3);
}
:deep(.aviso-readonly.atrasado) {
  background: #f8d7da;
  color: #842029;
  border: 1px solid rgba(204, 54, 54, 0.25);
}

:deep(.seccion-titulo) {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--d-brown);
  opacity: 0.8;
}
:deep(.bg-verde) {
  background-color: var(--d-green) !important;
}
:deep(.progress) {
  background-color: rgba(96, 108, 56, 0.15);
}

:deep(.actividades-lista) {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
:deep(.actividad-row) {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.15);
  border-radius: 8px;
  padding: 8px 10px;
}
:deep(.actividad-row.completada) {
  background: rgba(96, 108, 56, 0.05);
}
:deep(.estado-icono) {
  font-size: 1rem;
  margin-top: 1px;
  flex-shrink: 0;
}
:deep(.act-nombre) {
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--d-brown);
  display: flex;
  align-items: center;
  gap: 5px;
}
:deep(.orden-dot) {
  background: var(--d-green);
  color: var(--d-cream);
  font-size: 0.62rem;
  font-weight: 800;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
:deep(.reprog-chip) {
  display: inline-flex;
  align-items: center;
  background: #e8f5e9;
  color: #1b5e20;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
}
:deep(.obs-chip) {
  display: inline-flex;
  align-items: center;
  background: rgba(96, 108, 56, 0.08);
  color: var(--d-green);
  font-size: 0.72rem;
  font-style: italic;
  padding: 2px 8px;
  border-radius: 10px;
}
</style>
